/* eslint-disable react/prop-types */
import React, { memo, useState, useEffect } from 'react';
import { TouchableOpacity, View, Alert, Modal } from 'react-native';
import { QText } from './index';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '../hooks/MakeStylesHook';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import AttachmentIcon from './Icons/AttachmentIcon';
import CloseIcon from './Icons/CloseIcon';
import ImageIcon from './Icons/ImageIcon';
import FileIcon from './Icons/FileIcon';
import UniversalFileViewer from './UniversalFileViewer';
import { convertFileToBase64, validateFileSize } from '../utils/fileConversion';
import { FILE_UPLOAD_CONFIG, formatFileSize } from '../config/fileUpload';

const useStyles = makeStyles((theme) => ({
  container: {
    marginVertical: 10,
  },
  uploadButton: {
    borderWidth: 2,
    borderColor: theme.colors.primary,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.surface,
    minHeight: 80,
    // Add debugging styles
    position: 'relative',
    zIndex: 1,
  },
  uploadText: {
    color: theme.colors.primary,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  uploadSubText: {
    color: theme.colors.secondaryText,
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
  fileList: {
    marginTop: 10,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    padding: 10,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: theme.colors.outline,
  },
  fileInfo: {
    flex: 1,
    marginLeft: 10,
  },
  fileName: {
    color: theme.colors.secondaryText,
    fontSize: 14,
    fontWeight: '500',
  },
  fileSize: {
    color: theme.colors.secondaryText,
    fontSize: 12,
    opacity: 0.7,
    marginTop: 2,
  },
  removeButton: {
    padding: 8,
    borderRadius: 4,
  },
  labelHeading: {
    color: theme.colors.secondaryText,
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 20,
    minWidth: 300,
    maxWidth: '90%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.secondaryText,
    textAlign: 'center',
    marginBottom: 8,
  },
  modalSubtitle: {
    fontSize: 14,
    color: theme.colors.secondaryText,
    textAlign: 'center',
    marginBottom: 20,
    opacity: 0.7,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 15,
    borderRadius: 8,
    backgroundColor: theme.colors.background,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: theme.colors.outline,
  },
  modalOptionIcon: {
    marginRight: 15,
  },
  modalOptionText: {
    fontSize: 16,
    color: theme.colors.secondaryText,
    fontWeight: '500',
  },
  modalCancelButton: {
    padding: 15,
    borderRadius: 8,
    backgroundColor: theme.colors.outline,
    marginTop: 10,
  },
  modalCancelText: {
    fontSize: 16,
    color: theme.colors.secondaryText,
    textAlign: 'center',
  },
  // Preview modal styles
  previewOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  previewContainer: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 20,
    maxWidth: '95%',
    maxHeight: '90%',
    minWidth: 300,
  },
  previewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  previewTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.secondaryText,
    flex: 1,
    marginRight: 10,
  },
  previewCloseButton: {
    padding: 8,
  },
  previewContent: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: 400,
    borderRadius: 8,
  },
  previewText: {
    fontSize: 16,
    color: theme.colors.secondaryText,
    textAlign: 'center',
    marginBottom: 20,
  },
  previewFileInfo: {
    marginTop: 15,
    padding: 15,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
  },
  previewFileDetail: {
    fontSize: 14,
    color: theme.colors.secondaryText,
    marginBottom: 5,
  },
  previewText: {
    fontSize: 14,
    color: theme.colors.secondaryText,
    textAlign: 'center',
    marginBottom: 8,
  },
}));

const FileUpload = ({
  onFilesChange,
  disabled = false,
  maxFiles = FILE_UPLOAD_CONFIG.MAX_FILES,
  initialFiles = [],
  queueUserId,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedFiles, setSelectedFiles] = useState(initialFiles);
  const [showModal, setShowModal] = useState(false);
  const [fileViewerData, setFileViewerData] = useState(null);

  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      // Only set initialFiles if we don't have any frontend files with URIs
      // This prevents backend attachments (without URIs) from overriding frontend files (with URIs)
      const hasFrontendFiles = selectedFiles.some((file) => file.uri);
      if (!hasFrontendFiles) {
        setSelectedFiles(initialFiles);
      }
    }
  }, [initialFiles]);

  // Cleanup blob URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      // Cleanup any blob URLs from selected files
      selectedFiles.forEach((file) => {
        if (file.uri && file.uri.startsWith('blob:')) {
          URL.revokeObjectURL(file.uri);
        }
      });
    };
  }, [selectedFiles]);

  const getFileIcon = (mimeType, fileName) => {
    if (mimeType?.startsWith('image/')) {
      return <ImageIcon width={24} height={24} color={theme.colors.primary} />;
    }
    return <FileIcon width={24} height={24} color={theme.colors.primary} />;
  };

  const handleDocumentPick = async () => {
    try {
      // Check if DocumentPicker is available
      if (!DocumentPicker.getDocumentAsync) {
        console.error('DocumentPicker.getDocumentAsync is not available');
        Alert.alert(
          'Error',
          'Document picker is not available on this platform'
        );
        return;
      }

      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/plain',
        ],
        copyToCacheDirectory: true,
      });

      if (!result.canceled) {
        // Handle both old and new API formats
        let fileData;
        if (result.assets && result.assets.length > 0) {
          // New API format
          fileData = result.assets[0];
        } else {
          // Old API format
          fileData = result;
        }

        // Convert file to base64 (size validation happens inside)
        const conversionResult = await convertFileToBase64(fileData);

        if (!conversionResult.success) {
          Alert.alert(
            t('error') || 'Error',
            conversionResult.error ||
              t('file_conversion_error') ||
              'Failed to process document'
          );
          setShowModal(false);
          return;
        }

        const newFile = {
          id: Date.now() + Math.random(),
          name: fileData.name || 'Unknown Document',
          uri: conversionResult.dataUrl, // Use base64 data URI
          type:
            conversionResult.mimeType ||
            fileData.mimeType ||
            fileData.type ||
            'application/pdf',
          size:
            conversionResult.size || fileData.size || fileData.fileSize || 0,
        };

        const updatedFiles = [...selectedFiles, newFile].slice(0, maxFiles);
        setSelectedFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
      }
      setShowModal(false);
    } catch (error) {
      let errorMessage = t('file_pick_error') || 'Failed to pick document';

      if (error.message?.includes('size')) {
        errorMessage =
          t('file_too_large_error') ||
          'File is too large. Maximum size is 10MB';
      } else if (error.message?.includes('network')) {
        errorMessage =
          t('network_error') ||
          'Network error. Please check your connection and try again';
      } else if (
        error.message?.includes('format') ||
        error.message?.includes('type')
      ) {
        errorMessage =
          t('unsupported_format_error') ||
          'Unsupported file format. Please select a valid document';
      }

      Alert.alert(t('error') || 'Error', errorMessage);
    }
  };

  const handleImagePick = async () => {
    try {
      // Try DocumentPicker first for better filename preservation
      if (DocumentPicker.getDocumentAsync) {
        const result = await DocumentPicker.getDocumentAsync({
          type: [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/bmp',
          ],
          copyToCacheDirectory: true,
        });

        if (!result.canceled) {
          // Handle both old and new API formats
          let fileData;
          if (result.assets && result.assets.length > 0) {
            // New API format
            fileData = result.assets[0];
          } else {
            // Old API format
            fileData = result;
          }

          // Convert image to base64 (size validation happens inside)
          const conversionResult = await convertFileToBase64(fileData);

          if (!conversionResult.success) {
            Alert.alert(
              t('error') || 'Error',
              conversionResult.error ||
                t('image_conversion_error') ||
                'Failed to process image'
            );
            setShowModal(false);
            return;
          }

          // Use the original filename from DocumentPicker
          const filename = fileData.name || `image_${Date.now()}.jpg`;

          const newFile = {
            id: Date.now() + Math.random(),
            name: filename,
            uri: conversionResult.dataUrl, // Use base64 data URI
            type:
              conversionResult.mimeType ||
              fileData.mimeType ||
              fileData.type ||
              'image/jpeg',
            size:
              conversionResult.size || fileData.size || fileData.fileSize || 0,
          };

          const updatedFiles = [...selectedFiles, newFile].slice(0, maxFiles);
          setSelectedFiles(updatedFiles);
          onFilesChange?.(updatedFiles);
          setShowModal(false);
          return;
        }
      }

      // Fallback to ImagePicker if DocumentPicker fails or user cancels

      // Check if ImagePicker is available
      if (!ImagePicker.launchImageLibraryAsync) {
        console.error('ImagePicker.launchImageLibraryAsync is not available');
        Alert.alert('Error', 'Image picker is not available on this platform');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: FILE_UPLOAD_CONFIG.IMAGE_QUALITY,
        allowsMultipleSelection: false,
        selectionLimit: 1,
        // Try to preserve original filename
        presentationStyle: 'fullScreen',
        // Additional options that might help preserve filename
        base64: false, // Don't convert to base64 here, we'll do it ourselves
      });

      if (!result.canceled) {
        const asset = result.assets[0];

        // Convert image to base64 (size validation happens inside)
        const conversionResult = await convertFileToBase64(asset);

        if (!conversionResult.success) {
          Alert.alert(
            t('error') || 'Error',
            conversionResult.error ||
              t('image_conversion_error') ||
              'Failed to process image'
          );
          setShowModal(false);
          return;
        }

        // Improved filename preservation logic
        const getFilenameFromUri = (uri) => {
          if (!uri) return null;

          // Handle data URIs (they don't have meaningful filenames)
          if (uri.startsWith('data:')) {
            return null;
          }

          // Handle file:// URIs (temporary files from ImagePicker)
          if (uri.startsWith('file://')) {
            const pathParts = uri.split('/');
            const filenameWithParams = pathParts[pathParts.length - 1];
            const filename = filenameWithParams.split('?')[0];
            return filename;
          }

          // Handle other URI types
          try {
            const pathParts = uri.split('/');
            const filenameWithParams = pathParts[pathParts.length - 1];
            const filename = filenameWithParams.split('?')[0];
            return filename;
          } catch (error) {
            return null;
          }
        };

        const getExtensionFromMimeType = (mimeType) => {
          switch (mimeType) {
            case 'image/jpeg':
              return '.jpg';
            case 'image/png':
              return '.png';
            case 'image/gif':
              return '.gif';
            case 'image/webp':
              return '.webp';
            case 'image/bmp':
              return '.bmp';
            default:
              return '.jpg';
          }
        };

        // Try to preserve original filename with correct extension
        let filename = asset.fileName || asset.filename;

        // Try additional properties that might contain the filename
        if (!filename) {
          // Check for other possible filename properties
          const possibleNames = [
            asset.fileName,
            asset.filename,
            asset.name,
            asset.originalName,
            asset.displayName,
            asset.title,
          ].filter(Boolean);

          if (possibleNames.length > 0) {
            filename = possibleNames[0];
          }
        }

        if (!filename) {
          // Extract from URI if available
          const uriFilename = getFilenameFromUri(asset.uri);

          if (
            uriFilename &&
            uriFilename.length > 1 &&
            !uriFilename.match(/^[a-zA-Z]$/)
          ) {
            // Only use URI filename if it's not a single letter (like "Z")
            filename = uriFilename;
          } else {
            // Generate filename with correct extension based on MIME type
            const mimeType =
              conversionResult.mimeType ||
              asset.type ||
              asset.mimeType ||
              'image/jpeg';
            const extension = getExtensionFromMimeType(mimeType);
            filename = `image_${Date.now()}${extension}`;
          }
        } else {
          // If we have a filename but it might not have the correct extension
          const mimeType =
            conversionResult.mimeType ||
            asset.type ||
            asset.mimeType ||
            'image/jpeg';
          const correctExtension = getExtensionFromMimeType(mimeType);
          const currentExtension = filename
            .toLowerCase()
            .match(/\.(jpg|jpeg|png|gif|webp|bmp)$/);

          if (!currentExtension || currentExtension[0] !== correctExtension) {
            // Remove existing extension and add correct one
            const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
            filename = `${nameWithoutExt}${correctExtension}`;
          }
        }

        const newFile = {
          id: Date.now() + Math.random(),
          name: filename,
          uri: conversionResult.dataUrl, // Use base64 data URI
          type:
            conversionResult.mimeType ||
            asset.type ||
            asset.mimeType ||
            'image/jpeg',
          size: conversionResult.size || asset.fileSize || asset.size || 0,
        };

        const updatedFiles = [...selectedFiles, newFile].slice(0, maxFiles);
        setSelectedFiles(updatedFiles);
        onFilesChange?.(updatedFiles);
      }
      setShowModal(false);
    } catch (error) {
      let errorMessage = t('image_pick_error') || 'Failed to pick image';

      if (error.message?.includes('size')) {
        errorMessage =
          t('file_too_large_error') ||
          'Image is too large. Maximum size is 10MB';
      } else if (error.message?.includes('network')) {
        errorMessage =
          t('network_error') ||
          'Network error. Please check your connection and try again';
      } else if (
        error.message?.includes('format') ||
        error.message?.includes('type')
      ) {
        errorMessage =
          t('unsupported_image_format_error') ||
          'Unsupported image format. Please select JPG, PNG, or GIF';
      }

      Alert.alert(t('error') || 'Error', errorMessage);
    }
  };

  const showUploadOptions = () => {
    setShowModal(true);
  };

  const removeFile = (fileId) => {
    const updatedFiles = selectedFiles.filter((file) => file.id !== fileId);
    setSelectedFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
  };

  const handleFilePreview = async (file) => {
    // Use UniversalFileViewer for all file types
    if (file) {
      // Determine if this is a frontend file (has URI) or backend attachment (has ID)
      const isFrontendFile = !!file.uri;
      const isBackendAttachment = !file.uri && file.id;

      setFileViewerData({
        attachmentId: file.id, // Always use file.id as attachmentId
        name: file.name,
        mimeType: file.type,
        url: file.uri, // This will be null for backend attachments, which is correct
        isLocalFile: isFrontendFile, // Flag to indicate this is a local file with URI
      });
    }
  };

  return (
    <View style={styles.container}>
      <QText style={styles.labelHeading}>
        {t('attachments') || 'Attachments'}
      </QText>

      <TouchableOpacity
        style={[
          styles.uploadButton,
          disabled && { opacity: 0.5 },
          selectedFiles.length >= maxFiles && {
            borderColor: theme.colors.outline,
          },
        ]}
        onPress={() => {
          if (!disabled && selectedFiles.length < maxFiles) {
            showUploadOptions();
          }
        }}
        disabled={disabled || selectedFiles.length >= maxFiles}
      >
        <AttachmentIcon width={32} height={32} color={theme.colors.primary} />
        <QText style={styles.uploadText}>
          {selectedFiles.length >= maxFiles
            ? t('max_files_reached') || 'Maximum files reached'
            : t('tap_to_upload_files') || 'Tap to upload files'}
        </QText>
        <QText style={styles.uploadSubText}>
          {t('supported_formats') ||
            'Supports images, PDF, Word, Excel, and text files'}
        </QText>
      </TouchableOpacity>

      {selectedFiles.length > 0 && (
        <View style={styles.fileList}>
          {selectedFiles.map((file) => (
            <View key={file.id} style={styles.fileItem}>
              <TouchableOpacity
                style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}
                onPress={() => handleFilePreview(file)}
              >
                {getFileIcon(file.type, file.name)}
                <View style={styles.fileInfo}>
                  <QText style={styles.fileName} numberOfLines={1}>
                    {file.name}
                  </QText>
                  <QText style={styles.fileSize}>
                    {formatFileSize(file.size)}
                  </QText>
                </View>
              </TouchableOpacity>
              {!disabled && (
                <TouchableOpacity
                  style={styles.removeButton}
                  onPress={() => removeFile(file.id)}
                >
                  <CloseIcon
                    width={20}
                    height={20}
                    color={theme.colors.error}
                  />
                </TouchableOpacity>
              )}
            </View>
          ))}
        </View>
      )}

      {/* Custom Modal for file type selection */}
      <Modal
        visible={showModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <QText style={styles.modalTitle}>Upload File</QText>
            <QText style={styles.modalSubtitle}>
              Choose what type of file you want to upload
            </QText>

            {/* Image Option */}
            <TouchableOpacity
              style={styles.modalOption}
              onPress={() => {
                handleImagePick();
              }}
            >
              <View style={styles.modalOptionIcon}>
                <ImageIcon
                  width={24}
                  height={24}
                  color={theme.colors.primary}
                />
              </View>
              <QText style={styles.modalOptionText}>Photos & Images</QText>
            </TouchableOpacity>

            {/* Document Option */}
            <TouchableOpacity
              style={styles.modalOption}
              onPress={() => {
                handleDocumentPick();
              }}
            >
              <View style={styles.modalOptionIcon}>
                <FileIcon width={24} height={24} color={theme.colors.primary} />
              </View>
              <QText style={styles.modalOptionText}>
                Documents (PDF, Word, Excel, Text)
              </QText>
            </TouchableOpacity>

            {/* Cancel Button */}
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => {
                setShowModal(false);
              }}
            >
              <QText style={styles.modalCancelText}>Cancel</QText>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Universal File Viewer */}
      {fileViewerData && (
        <UniversalFileViewer
          visible={true}
          onClose={() => setFileViewerData(null)}
          attachment={{
            id: fileViewerData.attachmentId,
            name: fileViewerData.name,
            type: fileViewerData.mimeType,
            uri: fileViewerData.url,
            isLocalFile: fileViewerData.isLocalFile,
          }}
        />
      )}
    </View>
  );
};

export default memo(FileUpload);
